// @ts-nocheck
import React, { memo, useCallback, useState } from 'react';
import { EdgeLabelRenderer } from 'reactflow';
import PropTypes from 'prop-types';
import { Paper, Typography, useTheme, Box } from '@mui/material';
import { EMPTY_OBJECT } from '../../../const/CommonConst';

const ConnectionTooltipCard = ({ relation }) => {
  const theme = useTheme();
  if (!relation) return null;

  const rowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
  };

  return (
    <Box
      sx={{
        p: 0,
        minWidth: 180,
        backgroundColor: theme?.palette?.background?.paper,
      }}
    >
      <Box sx={rowStyle}>
        <Typography
          variant="body5"
          sx={{ color: theme?.palette?.landscape?.tooltipLabelColor }}
        >
          Relation Type
        </Typography>
        <Typography variant="body5" sx={{ color: theme?.palette?.grey?.T80 }}>
          {relation?.is_bidirectional ? 'Bi-Directional' : 'Uni-Directional'}
        </Typography>
      </Box>

      <Box sx={{ ...rowStyle, mt: 0.5 }}>
        <Typography
          variant="body5"
          sx={{ color: theme?.palette?.landscape?.tooltipLabelColor }}
        >
          Cardinality
        </Typography>
        <Typography variant="body5" sx={{ color: theme?.palette?.grey?.T80 }}>
          {relation?.cardinality || '—'}
        </Typography>
      </Box>
    </Box>
  );
};

ConnectionTooltipCard.propTypes = {
  relation: PropTypes.shape({
    is_bidirectional: PropTypes.bool,
    cardinality: PropTypes.string,
  }),
};

const CustomBezierEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = EMPTY_OBJECT,
  markerEnd = null,
  markerStart = null,
  data = null,
}) => {
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const dx = Math.abs(targetX - sourceX);
  const offset = Math.min(50, Math.max(10, dx * 0.25));

  const x1 = sourcePosition === 'right' ? sourceX + offset : sourceX - offset;
  const y1 = sourceY;
  const x2 = targetPosition === 'left' ? targetX - offset : targetX + offset;
  const y2 = targetY;

  const edgePath = `M ${sourceX},${sourceY} C ${x1},${y1} ${x2},${y2} ${targetX},${targetY}`;

  const labelX = 0.125 * sourceX + 0.375 * x1 + 0.375 * x2 + 0.125 * targetX;
  const labelY = 0.125 * sourceY + 0.375 * y1 + 0.375 * y2 + 0.125 * targetY;

  const relation = data?.relation;

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, [setIsHovered]);
  const handleMouseOut = useCallback(() => {
    setIsHovered(false);
  }, [setIsHovered]);

  return (
    <>
      {/* Wide transparent path for hit detection */}
      <path
        d={edgePath}
        fill="none"
        strokeWidth={20}
        stroke="transparent"
        style={{ cursor: 'pointer' }}
        className="react-flow__edge-interaction"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseOut}
        pointerEvents="stroke"
      />

      {/* Visible edge path */}
      <path
        id={id}
        d={edgePath}
        fill="none"
        className="react-flow__edge-path"
        strokeWidth={1.5}
        stroke={style?.stroke || theme?.palette?.landscape?.handleColor}
        style={{ ...style, strokeDasharray: 'none' }}
        markerEnd={markerEnd}
        markerStart={markerStart}
        pointerEvents="none"
      />

      {/* Edge label */}
      <EdgeLabelRenderer>
        <Paper
          className="landscape-edge-label"
          style={{
            transform: `translate(-50%, -120%) translate(${labelX}px,${labelY}px)`,
            opacity: isHovered ? 1 : 0,
            padding: '8px 10px',
            borderRadius: 6,
          }}
        >
          <ConnectionTooltipCard relation={relation} />
        </Paper>
      </EdgeLabelRenderer>
    </>
  );
};

CustomBezierEdge.propTypes = {
  id: PropTypes.string.isRequired,
  sourceX: PropTypes.number.isRequired,
  sourceY: PropTypes.number.isRequired,
  targetX: PropTypes.number.isRequired,
  targetY: PropTypes.number.isRequired,
  sourcePosition: PropTypes.string.isRequired,
  targetPosition: PropTypes.string.isRequired,
  style: PropTypes.oneOfType([PropTypes.object]),
  markerEnd: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  markerStart: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  data: PropTypes.shape({
    relation: PropTypes.shape({
      is_bidirectional: PropTypes.bool,
      cardinality: PropTypes.string,
    }),
  }),
};

export default memo(CustomBezierEdge);
